import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { settlementsApi, SettlementResponse, SettlementCounts, CollectableGarage, runCycleReasonText } from '../services/settlementsApi';
import { PageResponse } from '../services/ordersApi';
import ExceptionsTab, { ExceptionsData, countExceptions } from '../components/finance/ExceptionsTab';
import ProvidersTab from '../components/settlements/ProvidersTab';

// Spec 049 — tabbed settlement workspace grouped by required action.
const TABS: { key: string; label: string; status?: string; countKey?: keyof SettlementCounts }[] = [
  { key: 'needs', label: 'Needs action', status: 'DRAFT', countKey: 'needsAction' },
  { key: 'awaiting', label: 'Awaiting payment', status: 'AWAITING_PAYMENT', countKey: 'awaitingPayment' },
  { key: 'paid', label: 'Paid', status: 'PAID', countKey: 'paid' },
  { key: 'hold', label: 'On hold', status: 'ON_HOLD', countKey: 'onHold' },
  { key: 'void', label: 'Voided', status: 'VOID', countKey: 'voided' },
  // Spec 053 Flow D1 — detector only; count = orphans + aged clawbacks + open adjustments.
  { key: 'exceptions', label: 'Exceptions' },
  // Spec 053 Flow F7 — Garagat paying its recovery providers; no count, it has its own filters.
  { key: 'providers', label: 'Providers' },
];

const aed = (n: number) => `AED ${n.toLocaleString('en-AE', { minimumFractionDigits: 2 })}`;

const badgeClass = (s: string) =>
  s === 'PAID' ? 'approved' : s === 'VOID' ? 'rejected' : s === 'ON_HOLD' ? 'warning' : 'pending';

export default function SettlementsPage() {
  const auth = useAuth();
  const [tab, setTab] = useState('needs');
  const [data, setData] = useState<PageResponse<SettlementResponse> | null>(null);
  const [counts, setCounts] = useState<SettlementCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const activeStatus = TABS.find((t) => t.key === tab)?.status;

  // Spec 049 follow-up — garages with completed orders a manual run would settle right now.
  const [collectable, setCollectable] = useState<CollectableGarage[]>([]);
  const [readyOpen, setReadyOpen] = useState(false);
  const [generating, setGenerating] = useState<string | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);
  // #520 — why the last run produced nothing, so the button stops looking like it worked.
  const [generateNotice, setGenerateNotice] = useState<string | null>(null);

  const fetchCollectable = useCallback(async () => {
    if (!auth.token) return;
    try {
      setCollectable(await settlementsApi.getCollectable(auth.token));
    } catch (err) {
      console.error('Failed to fetch collectable garages:', err);
    }
  }, [auth.token]);

  // Spec 053 Flow D1 — exceptions (orphans, aged clawbacks, open adjustments); fetched up front
  // so the tab badge is right before the tab is opened.
  const [exceptions, setExceptions] = useState<ExceptionsData | null>(null);
  const [exceptionsLoading, setExceptionsLoading] = useState(true);
  const [exceptionsError, setExceptionsError] = useState<string | null>(null);

  const fetchExceptions = useCallback(async () => {
    if (!auth.token) return;
    setExceptionsLoading(true);
    setExceptionsError(null);
    try {
      const [orphans, clawbacks, adjustments] = await Promise.all([
        settlementsApi.getOrphans(auth.token),
        settlementsApi.getClawbacks(auth.token),
        settlementsApi.getOpenAdjustments(auth.token),
      ]);
      setExceptions({ orphans, clawbacks, adjustments });
    } catch (err) {
      console.error('Failed to fetch settlement exceptions:', err);
      setExceptionsError(err instanceof Error ? err.message : 'Failed to load exceptions');
    } finally {
      setExceptionsLoading(false);
    }
  }, [auth.token]);

  const fetchData = useCallback(async () => {
    if (!auth.token) return;
    setLoading(true);
    try {
      const [list, c] = await Promise.all([
        settlementsApi.listSettlements(
          { status: activeStatus, garageName: search || undefined, page, size: 20 }, auth.token),
        settlementsApi.getCounts(auth.token),
      ]);
      setData(list);
      setCounts(c);
    } catch (err) {
      console.error('Failed to fetch settlements:', err);
    } finally {
      setLoading(false);
    }
  }, [auth.token, activeStatus, search, page]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchCollectable(); }, [fetchCollectable]);
  useEffect(() => { fetchExceptions(); }, [fetchExceptions]);

  const handleGenerate = async (garageId: string) => {
    if (!auth.token) return;
    setGenerating(garageId);
    setGenerateError(null);
    setGenerateNotice(null);
    try {
      const result = await settlementsApi.runCycle(garageId, auth.token);
      // #520 — a run that generated nothing is a 202, not an error: say which no-op it was.
      setGenerateNotice(result.generated ? null : runCycleReasonText(result));
      await Promise.all([fetchCollectable(), fetchData(), fetchExceptions()]);
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : 'Failed to generate settlement');
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--admin-text)', margin: 0 }}>Settlements</h2>
        {counts && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
              Total outstanding
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#030c54' }}>{aed(counts.totalOutstanding)}</div>
          </div>
        )}
      </div>

      {/* #520 — OUTSIDE the panel's length guard: a refused run refetches `collectable`, and the
          garage it refused for usually drops off that list, so a notice rendered inside the guard
          unmounts in the same tick it is set. That is the silent no-op all over again. */}
      {generateNotice && (
        <div className="admin-info-banner info" role="status" data-testid="run-cycle-notice" style={{ marginBottom: '1rem' }}>
          {generateNotice}
        </div>
      )}

      {/* Ready to settle — garages a manual run would settle right now */}
      {collectable.length > 0 && (
        <div className="admin-card" style={{ marginBottom: '1rem' }}>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => setReadyOpen((o) => !o)}
          >
            <h3 className="admin-card-title" style={{ margin: 0 }}>
              Ready to settle ({collectable.length} garage{collectable.length === 1 ? '' : 's'})
            </h3>
            <span style={{ fontSize: '0.875rem', color: 'var(--admin-text-muted)' }}>
              {readyOpen ? 'Hide' : 'Show'}
            </span>
          </div>
          {generateError && <div className="error-message" style={{ margin: '0.75rem 0 0' }}>{generateError}</div>}
          {readyOpen && (
            <div className="admin-table-wrapper" style={{ marginTop: '0.75rem' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Garage</th>
                    <th style={{ textAlign: 'right' }}>Orders</th>
                    <th style={{ textAlign: 'right' }}>Gross</th>
                    <th style={{ textAlign: 'right' }}>Net (before adjustments)</th>
                    <th>Oldest order</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {collectable.map((g) => (
                    <tr key={g.garageId}>
                      <td style={{ fontWeight: 500 }}>
                        <Link to={`/garages/${g.garageId}`} className="admin-link">
                          {g.garageName || g.garageId.slice(0, 8)}
                        </Link>
                      </td>
                      <td style={{ textAlign: 'right' }}>{g.orderCount}</td>
                      <td style={{ textAlign: 'right' }}>{aed(g.grossTotal)}</td>
                      <td style={{ textAlign: 'right' }}>{aed(g.netTotal)}</td>
                      <td>{g.oldestCompletedAt ? new Date(g.oldestCompletedAt).toLocaleDateString() : '-'}</td>
                      {/* #520 — one non-void settlement per garage per Dubai day, so a garage already
                          settled for this period cannot be collected again until the next one. */}
                      <td>
                        {g.periodAlreadySettled ? (
                          <span className="admin-badge warning" data-testid="blocked-until-next-period"
                                title={g.existingSettlementNumber ? `Already settled by ${g.existingSettlementNumber}` : undefined}>
                            Blocked until next period
                          </span>
                        ) : (
                          <span className="admin-badge approved" data-testid="ready-to-settle">Ready</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="admin-btn admin-btn-outline admin-btn-sm"
                          disabled={generating !== null || g.periodAlreadySettled}
                          title={g.periodAlreadySettled
                            ? `A settlement for this period already exists${g.existingSettlementNumber ? `: ${g.existingSettlementNumber}` : ''}.`
                            : undefined}
                          onClick={() => handleGenerate(g.garageId)}
                        >
                          {generating === g.garageId ? 'Generating…' : 'Generate settlement'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tabs with counts */}
      <div className="admin-tabs" style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', borderBottom: '1px solid var(--admin-border, #e5e7eb)' }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setPage(0); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '0.625rem 1rem',
              fontFamily: 'inherit', fontSize: '0.875rem',
              fontWeight: tab === t.key ? 600 : 500,
              color: tab === t.key ? '#030c54' : 'var(--admin-text-muted, #6b7280)',
              borderBottom: tab === t.key ? '2px solid #fff200' : '2px solid transparent',
            }}
          >
            {t.label}
            {t.countKey
              ? (counts ? ` (${counts[t.countKey]})` : '')
              : t.key === 'exceptions'
                ? (exceptions ? ` (${countExceptions(exceptions)})` : '')
                : ''}
          </button>
        ))}
      </div>

      {tab === 'providers' ? (
        <ProvidersTab />
      ) : tab === 'exceptions' ? (
        <ExceptionsTab
          data={exceptions}
          loading={exceptionsLoading}
          error={generateError ?? exceptionsError}
          notice={generateNotice}
          running={generating}
          onRunSettlement={handleGenerate}
        />
      ) : (
      <div className="admin-card">
        <div className="admin-toolbar">
          <input
            type="text"
            className="admin-search-input"
            placeholder="Search by garage name..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          />
        </div>

        {loading ? (
          <div className="admin-loading"><div className="admin-loading-spinner" /></div>
        ) : !data || data.content.length === 0 ? (
          <div className="admin-empty-state"><p>No settlements in this tab.</p></div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Settlement #</th><th>Garage</th><th>Period</th><th>Orders</th>
                  <th>Gross</th><th>Net payable</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {data.content.map((s) => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 500 }}>{s.settlementNumber}</td>
                    <td>{s.garageName}</td>
                    <td>{s.periodStart} → {s.periodEnd}</td>
                    <td>{s.orderCount}</td>
                    <td>{aed(s.grossRevenue)}</td>
                    <td style={{ fontWeight: 600 }}>{aed(s.netPayable)}</td>
                    <td><span className={`admin-badge ${badgeClass(s.status)}`}>{s.status}</span></td>
                    <td><Link to={`/settlements/${s.id}`} className="admin-link">Open</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data && data.totalPages > 1 && (
          <div className="admin-pagination">
            <button className="admin-btn admin-btn-outline admin-btn-sm" disabled={data.number === 0} onClick={() => setPage(data.number - 1)}>Previous</button>
            <span style={{ fontSize: '0.875rem', color: 'var(--admin-text-muted)' }}>Page {data.number + 1} of {data.totalPages}</span>
            <button className="admin-btn admin-btn-outline admin-btn-sm" disabled={data.number + 1 >= data.totalPages} onClick={() => setPage(data.number + 1)}>Next</button>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
